import {Helmet} from '@dr.pogodin/react-helmet';
import {skipToken} from '@reduxjs/toolkit/query';
import {Parameter} from 'platform/components';
import {useDateTimeFormatter} from 'platform/locale';

import {defaultTo, isEmpty, isNil} from 'ramda';
import {isNilOrEmpty} from 'ramda-adjunct';

import {useGetUserQuery} from '@dms/api/customer';
import {useGetCoreExchangeQuery} from '@dms/api/warehouse';
import i18n from '@dms/i18n';
import {testIds, warehouseRoutes} from '@dms/routes';
import {
  DetailTemplate,
  DetailTemplateHeader,
  getUserName,
  NavigationItem,
  usePermissions,
} from '@dms/shared';
import {createHeaderFlags} from '@dms/warehouse/shared';
import {useWarehouseParams} from '@dms/warehouse/shared-components';

import {composePath, EMPTY_PLACEHOLDER, Nullish, parseDate} from 'shared/web';

import {General} from './(sections)/General/General';
import {useCoreExchangeDialogs} from './hooks/useCoreExchangeDialogs';
import {useCoreExchangeHeaderActions} from './hooks/useCoreExchangeHeaderActions';
import {useCoreExchangeHeaderLinks} from './hooks/useCoreExchangeHeaderLinks';

export function CoreExchangeDetail() {
  const {coreExchangeId} = useWarehouseParams();

  const formatDateTime = useDateTimeFormatter();
  const [canEditCoreExchange] = usePermissions({permissionKeys: ['coreExchangeEdit']});

  const {
    data: coreExchange,
    isLoading: isCoreExchangeLoading,
    isFetching: isCoreExchangeFetching,
    isError: hasCoreExchangeError,
    refetch: refetchCoreExchange,
  } = useGetCoreExchangeQuery({coreExchangeId}, {skip: isNilOrEmpty(coreExchangeId)});

  const createdById = coreExchange?.header.createdBy;
  const completedById = coreExchange?.header.completedBy;

  const {data: createdByUser} = useGetUserQuery(isNil(createdById) ? skipToken : {id: createdById});

  const {data: completedByUser} = useGetUserQuery(
    isNil(completedById) ? skipToken : {id: completedById}
  );

  const headerLinks = useCoreExchangeHeaderLinks(coreExchange?.header);
  const {handleActionClick, openGeneralSettings, openReturnSettings} = useCoreExchangeDialogs(
    coreExchangeId,
    coreExchange,
    refetchCoreExchange
  );
  const {headerActions, areActionsLoading, hasActionsError} = useCoreExchangeHeaderActions(
    coreExchangeId,
    handleActionClick,
    canEditCoreExchange,
    isCoreExchangeFetching
  );

  const formatToShortDate = (date: string | Nullish) => {
    if (isNil(date) || isEmpty(date)) {
      return EMPTY_PLACEHOLDER;
    }
    return formatDateTime('dateShort', parseDate(date));
  };

  const headerParameters: Parameter[] = [
    `${i18n.t('general.labels.createdBy')}: ${defaultTo(EMPTY_PLACEHOLDER, getUserName(createdByUser))}`,
    `${i18n.t('general.labels.createdAt')}: ${formatToShortDate(coreExchange?.header.createdAt)}`,
    `${i18n.t('general.labels.completedBy')}: ${defaultTo(EMPTY_PLACEHOLDER, getUserName(completedByUser))}`,
    `${i18n.t('general.labels.completedAt')}: ${formatToShortDate(coreExchange?.header.completedAt)}`,
    ...headerLinks,
  ];

  const header: DetailTemplateHeader = {
    title: i18n.t('entity.warehouse.labels.coreExchange'),
    icon: 'action/autorenew',
    isEntityLoading: isCoreExchangeFetching,
    parameters: headerParameters,
    actions: headerActions,
    flags: createHeaderFlags(
      coreExchange?.markers?.map((marker) => ({
        title: defaultTo('', marker.title),
        color: marker.color,
      }))
    ),
  };

  const navigation: NavigationItem[] = [
    {
      id: 'general',
      label: i18n.t('general.labels.general'),
      href: composePath(warehouseRoutes.coreExchangeDetailOverview, {
        params: {id: coreExchangeId},
      }),
      content: (
        <General
          coreExchange={coreExchange}
          onEditGeneral={openGeneralSettings}
          onEditReturnSettings={openReturnSettings}
          isEditable={canEditCoreExchange}
          data-testid={testIds.warehouse.coreExchangeDetailOverview('section.general')}
        />
      ),
      'data-testid': testIds.warehouse.coreExchangeDetailOverview('navigation.general'),
    },
  ];

  return (
    <>
      <Helmet title={i18n.t('entity.warehouse.labels.coreExchange')} />
      <DetailTemplate
        isLoading={isCoreExchangeLoading || areActionsLoading}
        isError={hasCoreExchangeError || hasActionsError}
        header={header}
        navigation={navigation}
        data-testid={testIds.warehouse.coreExchangeDetailOverview('page')}
      />
    </>
  );
}
